import React, { CSSProperties, useEffect } from 'react';

import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { accountSelector } from '../account/account-selectors';
import { loadAccount } from '../account/actions/load-account';

import { AnnualSubscriptionRenewalDialogButton } from '../account/components/annual-subscription-renewal-dialog-button';
import { UiButton } from '../ui/ui-button';
import { UiMessageBanner } from '../ui/ui-message-banner';
import { accountBilling, accountCarrierRegistration } from '../web-routes';

import { ACCOUNT_STATUS } from '../util/account-status';
import { SubscriptionCadence } from '../util/subscription-cadence';

type InputProps = {
  accountId: number;
  style?: React.CSSProperties;
};

const mapStateToProps =
  ({ accountId }: Pick<InputProps, 'accountId'>) =>
  (state: any) => ({
    account: accountSelector(state, accountId),
  });

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: Pick<InputProps, 'accountId'>,
) => ({
  actions: {
    loadAccount: () => dispatch(loadAccount(accountId)),
  },
});

type Props = InputProps;

const trialEndedMessageComponents = (account: TeamEngineAccount) => ({
  backgroundColor: 'u-background-messageError',
  messageComponent: (
    <div className="u-display-inline">
      Your trial has ended. Subscribe to continue using Team Engine.
    </div>
  ),
  smallMessageComponent: <div className="u-display-inline">Your trial has ended.</div>,
  buttonComponent: (
    <UiButton
      linkTo={accountBilling(account.accountId)}
      variant="primary"
      size="xs"
      className="u-margin-left"
    >
      Subscribe Now
    </UiButton>
  ),
});

const trialMessageComponents = (account: TeamEngineAccount) => {
  const trialEndDate = moment(account.trialEndDate || new Date());
  const todaysDate = moment().startOf('day');
  const trialDaysRemaining = Math.round(moment.duration(trialEndDate.diff(todaysDate)).as('days'));
  return {
    backgroundColor:
      trialDaysRemaining > 7 ? 'u-background-lightGray' : 'u-background-messageWarning',
    messageComponent: (
      <div className="u-display-inline">
        You have{' '}
        <strong className="u-textDecoration-underline">
          {trialDaysRemaining > 0 ? trialDaysRemaining : 0} day{trialDaysRemaining !== 1 ? 's' : ''}
        </strong>{' '}
        left in your trial ending {trialEndDate.format('MMMM Do')}
      </div>
    ),
    smallMessageComponent: (
      <div className="u-display-inline">
        <strong className="u-textDecoration-underline">
          {trialDaysRemaining > 0 ? trialDaysRemaining : 0} day{trialDaysRemaining !== 1 ? 's' : ''}
        </strong>{' '}
        left in your trial
      </div>
    ),
    buttonComponent: (
      <UiButton
        linkTo={accountBilling(account.accountId)}
        variant="primary"
        size="xs"
        className="u-margin-left"
      >
        Subscribe Now
      </UiButton>
    ),
  };
};

const canceledMessageComponents = (account: TeamEngineAccount) => {
  if (!account.nextBaseSubscriptionBillingDate) {
    return null;
  }

  const endDate = moment(account.nextBaseSubscriptionBillingDate);
  const todaysDate = moment().startOf('day');
  const subscriptionDaysRemaining = Math.floor(
    moment.duration(endDate.diff(todaysDate)).as('days'),
  );

  // Don't show canceled banner if they still have more than 30 days (i.e. annual subscribers who have canceled earlier)
  if (subscriptionDaysRemaining > 30) {
    return null;
  }

  return {
    backgroundColor:
      subscriptionDaysRemaining > 7 ? 'u-background-lightGray' : 'u-background-messageWarning',
    messageComponent: (
      <div className="u-display-inline">
        Your subscription ends in{' '}
        <strong>{subscriptionDaysRemaining > 0 ? subscriptionDaysRemaining : 0}</strong> day
        {subscriptionDaysRemaining !== 1 ? 's' : ''}
      </div>
    ),
    smallMessageComponent: (
      <div className="u-display-inline">
        Your subscription ends in{' '}
        <strong>{subscriptionDaysRemaining > 0 ? subscriptionDaysRemaining : 0}</strong> day
        {subscriptionDaysRemaining !== 1 ? 's' : ''}
      </div>
    ),
    buttonComponent: (
      <UiButton
        linkTo={accountBilling(account.accountId)}
        variant="primary"
        size="xs"
        className="u-margin-left"
      >
        Subscribe Now
      </UiButton>
    ),
  };
};

const annualSubscriptionReminderComponents = (account: TeamEngineAccount) => {
  if (!account.nextBaseSubscriptionBillingDate) {
    return null;
  }

  const endDate = moment(account.nextBaseSubscriptionBillingDate);
  const todaysDate = moment().startOf('day');
  const subscriptionDaysRemaining = Math.floor(
    moment.duration(endDate.diff(todaysDate)).as('days'),
  );

  if (account.lastRespondedToAnnualRenewal) {
    // Stop-gap solution: if an account last responded within the past 11 months, then don't show message.
    // Theoretically should be just 30 days, but this is to play it safe.
    const lastRespondedDate = moment(account.lastRespondedToAnnualRenewal);
    if (Math.floor(moment.duration(todaysDate.diff(lastRespondedDate)).as('days')) < 335) {
      return null;
    }
  }

  if (subscriptionDaysRemaining > 30) {
    return null;
  }

  return {
    backgroundColor: 'u-background-messageWarning',
    messageComponent: (
      <div className="u-display-inline u-margin-right-md">
        Your annual subscription ends in{' '}
        <strong>{subscriptionDaysRemaining > 0 ? subscriptionDaysRemaining : 0}</strong> day
        {subscriptionDaysRemaining !== 1 ? 's' : ''} on {endDate.format('MMMM Do')}
      </div>
    ),
    smallMessageComponent: (
      <div className="u-display-inline u-margin-right-md">
        Annual subscription ends on <strong>{endDate.format('MMMM Do, YYYY')}</strong>
      </div>
    ),
    buttonComponent: (
      <AnnualSubscriptionRenewalDialogButton accountId={account.accountId} size="xs" />
    ),
  };
};

const DeliverabilityMessage = ({
  account,
}: {
  account: TeamEngineAccount;
  style?: CSSProperties;
}) => {
  const isCustomerWithoutBanner = [ACCOUNT_STATUS.PAYING_CUSTOMER, ACCOUNT_STATUS.CLOSED].includes(
    account.status,
  );
  if (!isCustomerWithoutBanner) {
    return null;
  }
  if (!accountWillShowDeliverabilityMessage(account)) {
    return null;
  }
  return (
    <UiMessageBanner
      variant="error"
      className="accountStatusMessageBar u-background-messageError u-margin-bottom-5px"
      action={{ name: 'Add Now', link: accountCarrierRegistration(account.accountId) }}
    >
      <div className="typo-bold u-flex-center">
        Your company&apos;s EIN is required to continue using text messaging
      </div>
    </UiMessageBanner>
  );
};

export const accountWillShowDeliverabilityMessage = (account: TeamEngineAccount) =>
  !account.businessRegistrationNumber;

export const AccountStatusMessage = ({ accountId }: Props) => {
  let components;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { account } = useSelector(mapStateToProps({ accountId }), shallowEqual);
  const { actions } = mapDispatchToProps(useDispatch(), { accountId });

  useEffect(() => {
    actions.loadAccount();
  });

  if (!account) {
    return null;
  }

  switch (account.status) {
    case ACCOUNT_STATUS.TRIAL_LAPSED:
      components = trialEndedMessageComponents(account);
      break;
    case ACCOUNT_STATUS.TRIAL:
      components = trialMessageComponents(account);
      break;
    case ACCOUNT_STATUS.CANCELED:
      components = canceledMessageComponents(account);
      break;
    case ACCOUNT_STATUS.PAYING_CUSTOMER:
      if (account.subscriptionCadence === SubscriptionCadence.ANNUAL) {
        components = annualSubscriptionReminderComponents(account);
      }
      break;
    default:
      components = null;
  }

  if (!components || !account.trialEndDate) {
    return <DeliverabilityMessage account={account} />;
  }

  return (
    <UiMessageBanner
      variant="error"
      className={`accountStatusMessageBar ${components.backgroundColor} u-margin-bottom-5px`}
    >
      <div className="typo-bold u-flex-center">
        {isSmallScreen ? components.smallMessageComponent : components.messageComponent}
        {components.buttonComponent}
      </div>
    </UiMessageBanner>
  );
};
